import { Button, CircularProgress, IconButton, Paper, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Edit, Email } from '@material-ui/icons';
import GetAppIcon from '@material-ui/icons/GetApp';
import { API } from 'aws-amplify';
import * as FileSaver from 'file-saver';
import { useState } from 'react';
// Material
import { useSelector } from 'react-redux';

import { CustomTable } from '../../pages/ApplicationsList/applications-list.styles';
import { EnumAppSignatureFormat, EnumAppSignatureType, EnumSections, MAppSignature } from '../../redux/scholarship.models';
// Components
import { selectSendingEmail } from '../../redux/scholarship.selectors';
import UploadButton from '../UploadForm/upload-button';

interface ThirdPartyGridProps {
  signatures: MAppSignature[];
  handleEdit: (signatureId: string) => void;
  resendEmail: (signature: MAppSignature) => void;
  handleSignature: (appId: string, signature: MAppSignature) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontWeight: 800
    },
    buttonSmall: {
      marginLeft: '10px',
      height: '25px',
      font: '15px;',
      backgroundColor: '#f1f1f1'
    },
    emailProgress: {
      color: green[500],
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -11,
      marginLeft: -11
    },
    buttonDownload: {
      height: '30px',
      font: '15px;',
      backgroundColor: '#3f51b5'
    }
  })
);

export const ThirdPartyGrid = ({ signatures, handleEdit, resendEmail, handleSignature }: ThirdPartyGridProps): JSX.Element => {
  const classes = useStyles();
  const sendingEmail: boolean = useSelector(selectSendingEmail);
  const [downloading, setDownloading] = useState(false);

  const base64toBlob = (base64Data: string, contentType: string): Blob => {
    contentType = contentType || '';
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array<Uint8Array>(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes = new Array<number>(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }

    return new Blob(byteArrays, { type: contentType });
  };

  const handleFormDownload = (row: MAppSignature) => {
    setDownloading(true);

    API.post('service-mtpf', '/signature-pdf', {
      body: {
        userId: row.userId,
        appId: row.id,
        fileType: EnumSections.SIGNATURE,
        signatureType: EnumAppSignatureType.PURCHASER,
        signatureId: row.signatureId
      }
    })
      .then((response) => {
        console.log(response);
        const blob = base64toBlob(response.data.base64, response.data.mime);
        FileSaver.saveAs(blob, response.data.filename);
        setDownloading(false);
      })
      .catch((error) => {
        console.error(error);
        setDownloading(false);
      });
  };

  return (
    <>
      <TableContainer component={Paper} style={{ marginTop: '20px' }}>
        <CustomTable>
          <TableHead style={{ backgroundColor: '#f3f6fd' }}>
            <TableRow>
              <TableCell className={classes.title}>Name</TableCell>
              <TableCell className={classes.title}>Format</TableCell>
              <TableCell className={classes.title} />
              <TableCell className={classes.title}>Upload Signed Form</TableCell>
              <TableCell className={classes.title} align="right">
                Status
              </TableCell>
              <TableCell className={classes.title} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {signatures.map((row: MAppSignature) => (
              <TableRow key={row.signatureId} hover style={{ cursor: 'pointer' }}>
                <TableCell component="th" scope="row">
                  {row.firstName} {row.lastName}
                </TableCell>
                <TableCell>{row.signatureFormat === EnumAppSignatureFormat.DIGITAL ? 'Email' : 'Paper'}</TableCell>
                <TableCell>
                  {row.signatureFormat === EnumAppSignatureFormat.DIGITAL ? (
                    row.email
                  ) : (
                    <Button variant="contained" color="primary" onClick={() => handleFormDownload(row)} className={classes.buttonDownload}>
                      <GetAppIcon /> Download Form
                      {downloading && (
                        <span>
                          &emsp;
                          <CircularProgress size={12} color={'inherit'} />
                        </span>
                      )}
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  {row.signatureFormat === EnumAppSignatureFormat.DIGITAL ? undefined : (
                    <UploadButton
                      appId={row.id}
                      accept={['image/*', 'application/pdf']}
                      dataSource={row}
                      disabled={!!row.filePath}
                      noClick={true}
                      noDrag={true}
                      noKeyboard={true}
                      section={'signature'}
                      userId={row.userId}
                      handleSignature={handleSignature}
                    />
                  )}
                </TableCell>
                <TableCell align="right">{row.signed ? 'Signed' : 'Pending'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size={'small'}
                    onClick={() => row.signatureId && handleEdit(row.signatureId)}
                    title={'Edit'}
                    disabled={!row.signatureId}
                  >
                    <Edit />
                  </IconButton>
                  {row.signatureFormat === EnumAppSignatureFormat.DIGITAL && (
                    <IconButton size={'small'} title={'Send Email Again'} onClick={() => resendEmail(row)}>
                      <Email />
                      {sendingEmail && <CircularProgress size={22} className={classes.emailProgress} />}
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </CustomTable>
      </TableContainer>
    </>
  );
};

export default ThirdPartyGrid;
